import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, Autocomplete, DirectionsRenderer } from '@react-google-maps/api';
import { FaMapMarkerAlt, FaTruck, FaCarSide, FaShuttleVan, FaTruckMoving, FaTruckMonster, FaTruckPickup, FaTruckLoading } from 'react-icons/fa';

const containerStyle = {
  width: '100%',
  height: '100vh'
};

const defaultCenter = {
  lat: -21.5556,
  lng: -45.4364
};

export default function NewRoute() {
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [midpoints, setMidpoints] = useState(['']);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [originMarker, setOriginMarker] = useState(null);
  const [destMarker, setDestMarker] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const vehicleAxles = {
    fiorino: 2,
    van: 2,
    hr: 2,
    truck34: 3,
    toco: 4,
    truck: 5,
    bitruck: 6,
    carreta: 7,
  };

  const [costKm, setCostKm] = useState('');
  const [dailyDriver, setDailyDriver] = useState('');
  const [dailyHelper, setDailyHelper] = useState('');
  const [extraCosts, setExtraCosts] = useState('');
  const [routeIndex, setRouteIndex] = useState(0);
  const [tollLocations, setTollLocations] = useState([]);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [averageDeliveryTime, setAverageDeliveryTime] = useState('');
  const [cargoWeight, setCargoWeight] = useState('');
  const [startTime, setStartTime] = useState("08:00");
  // New state for interface toggle
  const [showInterface, setShowInterface] = useState(true);
  const [exportFormat, setExportFormat] = useState('');

  const timeOptions = [];
  for (let h = 6; h <= 17; h++) {
    ["00", "30"].forEach(min => {
      if (h === 17 && min !== "00") return;
      const timeStr = `${h < 10 ? "0" + h : h}:${min}`;
      timeOptions.push(timeStr);
    });
  }

  // NEW: Define vehicle max weight (in kg) and icon mappings, plus a name formatter.
  const vehicleMaxWeights = {
    fiorino: 500,
    van: 1000,
    hr: 1500,
    truck34: 4000, // Represents "3/4"
    toco: 6000,
    truck: 10000,
    bitruck: 19000,
    carreta: 24000,
  };

  const vehicleIcons = {
    fiorino: FaTruckPickup,
    van: FaShuttleVan,
    hr: FaCarSide,
    truck34: FaTruckMoving,
    toco: FaTruck,
    truck: FaTruckMonster,
    bitruck: FaTruckLoading,
    carreta: FaTruck,
  };

  const formatVehicleName = (type) => type === "truck34" ? "3/4" : type.charAt(0).toUpperCase() + type.slice(1);

  const cargoWeightNum = parseFloat(cargoWeight);
  const availableVehicles = Object.keys(vehicleAxles).filter(v => !cargoWeightNum || cargoWeightNum <= vehicleMaxWeights[v]);

  async function handleRoute() {
    const costKmVal = parseFloat(costKm) || 0;
    const dailyDriverVal = parseFloat(dailyDriver) || 0;
    const dailyHelperVal = parseFloat(dailyHelper) || 0;
    const extraCostsVal = parseFloat(extraCosts) || 0;
    if (!origin || !destination) return;
    const directionsService = new window.google.maps.DirectionsService();
    const waypoints = midpoints
      .filter(point => point.trim() !== '')
      .map(point => ({ location: point, stopover: true }));
    const result = await directionsService.route({
      origin,
      destination,
      waypoints,
      optimizeWaypoints: true, // optimize intermediate points
      travelMode: window.google.maps.TravelMode.DRIVING,
      provideRouteAlternatives: true,
    });
    setDirectionsResponse(result);

    if (result?.routes) {
      const results = result.routes.map(route => {
        const newKm = route.legs.reduce((sum, leg) => sum + leg.distance.value, 0) / 1000;
        const newTotal = newKm * costKmVal + dailyDriverVal + dailyHelperVal + extraCostsVal;
        const routeTime = route.legs.reduce((sum, leg) => sum + leg.duration.value, 0) / 60;
        // Use 15 minutes as default delivery time if campo estiver vazio
        const driverDelivery = averageDeliveryTime.trim() === '' ? 15 : parseFloat(averageDeliveryTime);
        return { newTotal, newKm, totalTime: routeTime + driverDelivery };
      });
      setComparisonResults(results);
    }
  }

  async function fetchTolls() {
    try {
      const response = await fetch('https://overpass-api.de/api/interpreter?data=[out:json];node["barrier"="toll_booth"](area:3600065603);out body;');
      const data = await response.json();
      const tolls = data.elements.map(element => ({
        lat: element.lat,
        lng: element.lon,
        description: 'Pedágio'
      }));
      setTollLocations(tolls);
    } catch (error) {
      console.error('Error fetching tolls:', error);
    }
  }

  const handleMidpointChange = (e, index) => {
    let newMidpoints = [...midpoints];
    newMidpoints[index] = e.target.value;
    if (index === midpoints.length - 1 && e.target.value.trim() !== '') {
      newMidpoints.push('');
    }
    setMidpoints(newMidpoints);
  };

  const handleExport = () => {
    // Gather summary data
    const summaryData = {
      origin,
      destination,
      midpoints: midpoints.filter(p => p.trim() !== ''),
      selectedVehicle: selectedVehicle ? formatVehicleName(selectedVehicle.type) : '',
      cargoWeight,
      costKm,
      dailyDriver,
      dailyHelper,
      extraCosts,
      averageDeliveryTime,
      startTime,
    };
    // Gather directions steps, if available
    let directionsData = [];
    if (directionsResponse?.routes) {
      directionsResponse.routes.forEach((route, rIdx) => {
        route.legs.forEach((leg, lIdx) => {
          leg.steps.forEach((step, sIdx) => {
            directionsData.push({
              route: rIdx + 1,
              leg: lIdx + 1,
              step: sIdx + 1,
              instruction: step.html_instructions || '',
              maneuver: step.maneuver || '',
              distance: step.distance?.text || '',
              duration: step.duration?.text || '',
              start_location: step.start_location || {},
              end_location: step.end_location || {}
            });
          });
        });
      });
    }
    // NEW: Concatenate directions of each route into a single Portuguese string
    let routesDirections = [];
    if (directionsResponse?.routes) {
      routesDirections = directionsResponse.routes.map(route => {
        let instructions = [];
        route.legs.forEach(leg => {
          leg.steps.forEach(step => {
            // Remove possíveis tags HTML e juntar em português
            instructions.push(step.html_instructions
              ? step.html_instructions.replace(/<\/?[^>]+(>|$)/g, "")
              : "");
          });
        });
        return instructions.join(', ');
      });
    }
    const exportData = { summary: summaryData, directions: directionsData, routes_directions: routesDirections };

    if (exportFormat === "json") {
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = "route_export.json";
      a.click();
    } else if (exportFormat === "csv" || exportFormat === "excell") {
      let csvContent = "";
      if (exportFormat === "csv") {
        csvContent += "Field,Value\n";
        for (let key in summaryData) {
          csvContent += `${key},${summaryData[key]}\n`;
        }
        csvContent += "\nRoute Directions\n";
        csvContent += "Route,Leg,Step,Instruction,Maneuver,Distance,Duration,Start_Lat,Start_Lng,End_Lat,End_Lng\n";
        directionsData.forEach(item => {
          csvContent += `${item.route},${item.leg},${item.step},"${item.instruction.replace(/"/g, '""')}","${item.maneuver}",${item.distance},${item.duration},${item.start_location.lat || ''},${item.start_location.lng || ''},${item.end_location.lat || ''},${item.end_location.lng || ''}\n`;
        });
      } else if (exportFormat === "excell") {
        // Build summary table: header row with keys and value row with corresponding values.
        csvContent += "Resumo\n";
        csvContent += Object.keys(summaryData).join(",") + "\n";
        csvContent += Object.values(summaryData).join(",") + "\n\n";

        // Build detailed directions table.
        csvContent += "Detalhes das Rotas\n";
        csvContent += "Route,Leg,Step,Instruction,Maneuver,Distance,Duration,Start_Lat,Start_Lng,End_Lat,End_Lng\n";
        directionsData.forEach(item => {
          csvContent += `${item.route},${item.leg},${item.step},"${item.instruction.replace(/"/g, '""')}","${item.maneuver}",${item.distance},${item.duration},${item.start_location.lat || ''},${item.start_location.lng || ''},${item.end_location.lat || ''},${item.end_location.lng || ''}\n`;
        });

        // Build table with concatenated directions from each route.
        csvContent += "\nResumo das Direções\n";
        csvContent += "Rota,Direções\n";
        routesDirections.forEach((rd, idx) => {
          csvContent += `Rota ${idx + 1},${rd}\n`;
        });
      }
      const blob = new Blob([csvContent], { type: "text/csv" });
      const a = document.createElement('a');
      const ext = exportFormat === "csv" ? "csv" : "xls";
      a.href = URL.createObjectURL(blob);
      a.download = `route_export.${ext}`;
      a.click();
    }
  };

  useEffect(() => {
    fetchTolls();
  }, []);

  return (
    <LoadScript googleMapsApiKey="AIzaSyAJgaLbnPer72CcptLqrmvvIM_y7y1rLEI" libraries={['places']}>
      <GoogleMap mapContainerStyle={containerStyle} center={mapCenter} zoom={8}>
        {/* Toggle interface button */}
        <button
          onClick={() => setShowInterface(prev => !prev)}
          style={{
            position: 'absolute',
            top: '10px',
            left: '205px',
            zIndex: 5,
            padding: '8px 12px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#2196f3',
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          {showInterface ? "Ocultar Interface" : "Mostrar Interface"}
        </button>
        {showInterface && (
          <div className="absolute top-16 left-48 transform -translate-x-1/2 bg-white-whiter p-4 rounded-lg shadow-md max-h-[calc(100vh-6rem)] overflow-y-auto">
            <div className="flex items-center mb-4">
              <FaMapMarkerAlt className="mr-2" />
              <Autocomplete onPlaceChanged={() => setOrigin(document.getElementById('origin').value)}>
                <input id="origin" type="text" placeholder="Ponto de partida" className="w-72 p-2 rounded border border-gray-300" />
              </Autocomplete>
            </div>

            {midpoints.map((point, index) => (
              <div className="flex items-center mb-4" key={index} style={{ marginLeft: '20px' }}>
                <FaMapMarkerAlt className="mr-2" style={{ opacity: 0.6 }} />
                <Autocomplete onPlaceChanged={() => {
                  const inputValue = document.getElementById(`midpoint-${index}`).value;
                  handleMidpointChange({ target: { value: inputValue } }, index);
                }}>
                  <input
                    id={`midpoint-${index}`}
                    type="text"
                    placeholder="Ponto intermediário"
                    value={point}
                    onChange={(e) => handleMidpointChange(e, index)}
                    className="w-64 p-2 rounded border border-gray-300"
                  />
                </Autocomplete>
              </div>
            ))}

            <div className="flex items-center mb-4">
              <FaMapMarkerAlt className="mr-2" />
              <Autocomplete onPlaceChanged={() => setDestination(document.getElementById('destination').value)}>
                <input id="destination" type="text" placeholder="Ponto de chegada" className="w-72 p-2 rounded border border-gray-300" />
              </Autocomplete>
            </div>

            <div className="text-center mb-4">
              <button
                onClick={handleRoute}
                className="bg-salBlue text-white border-none p-2 rounded cursor-pointer"
              >
                Inserir Rota
              </button>
            </div>
            <hr className="border-gray-300 mb-4" />
            <div className="mb-4">
              {directionsResponse?.routes?.slice(0, 3).map((route, idx) => {
                const leg = route.legs?.[0];
                const newKm = leg.distance.value / 1000;
                const newTotal = newKm * (parseFloat(costKm) || 0) + (parseFloat(dailyDriver) || 0) + (parseFloat(dailyHelper) || 0) + (parseFloat(extraCosts) || 0);

                return (
                  <div
                    key={idx}
                    onClick={() => setRouteIndex(idx)}
                    className={`border ${routeIndex === idx ? 'border-salBlue' : 'border-gray-300'} p-2 cursor-pointer mb-2 text-center`}
                  >
                    <p>Rota {idx + 1}</p>
                    <p>Distância: {leg?.distance?.text}</p>
                    <p>Duração: {leg?.duration?.text}</p>
                    <p>Custo Total: R$ {isNaN(newTotal) ? '0.00' : newTotal.toFixed(2)}</p>
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <button
                onClick={() => setShowAdvancedFilters(prev => !prev)}
                className="w-full text-left bg-gray-200 p-2 rounded"
              >
                Filtros Avançados {showAdvancedFilters ? '▲' : '▼'}
              </button>
              {showAdvancedFilters && (
                <div className="mt-2 p-2 border rounded w-80">
                  <div className="flex flex-col gap-2 mb-2">
                    <label className="block">
                      Preço/Km
                      <input
                        type="number"
                        placeholder="Preço/Km"
                        value={costKm}
                        onChange={(e) => setCostKm(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      />
                    </label>
                    <label className="block">
                      Diária Motorista
                      <input
                        type="number"
                        placeholder="Diária Motorista"
                        value={dailyDriver}
                        onChange={(e) => setDailyDriver(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      />
                    </label>
                    <label className="block">
                      Diária Ajudante
                      <input
                        type="number"
                        placeholder="Diária Ajudante"
                        value={dailyHelper}
                        onChange={(e) => setDailyHelper(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      />
                    </label>
                    <label className="block">
                      Custos Extras
                      <input
                        type="number"
                        placeholder="Custos Extras"
                        value={extraCosts}
                        onChange={(e) => setExtraCosts(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      />
                    </label>
                    <label className="block">
                      Tempo médio de entrega (minutos)
                      <input
                        type="number"
                        placeholder="Tempo médio de entrega (minutos)"
                        value={averageDeliveryTime}
                        onChange={(e) => setAverageDeliveryTime(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      />
                    </label>
                    <label className="block">
                      Horário de início
                      <select
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      >
                        {timeOptions.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="block w-2/3">
                      Peso da carga (kg)
                      <input
                        type="number"
                        placeholder="Peso da carga (kg)"
                        value={cargoWeight}
                        onChange={(e) => setCargoWeight(e.target.value)}
                        className="border border-gray-300 rounded p-2 w-full"
                      />
                    </label>
                    <label className="block w-1/3">
                      Veículo
                      <select
                        value={selectedVehicle?.type || ''}
                        onChange={(e) => {
                          const type = e.target.value;
                          setSelectedVehicle({ type, axles: vehicleAxles[type] });
                        }}
                        className="border border-gray-300 rounded p-2 w-full"
                      >
                        <option value="">Selecione o veículo</option>
                        {availableVehicles.map(veh => (
                          <option key={veh} value={veh}>{formatVehicleName(veh)}</option>
                        ))}
                      </select>
                      {/* Display selected vehicle and its icon at the right menu */}
                      {selectedVehicle && (
                        <div className="mt-2 flex items-center gap-2">
                          {React.createElement(vehicleIcons[selectedVehicle.type], { className: "text-xl" })}
                          <span>{formatVehicleName(selectedVehicle.type)}</span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
        {directionsResponse && <DirectionsRenderer directions={directionsResponse} routeIndex={routeIndex} />}
        {/* New marker to display delivery time over the route's starting point */}
        {directionsResponse?.routes?.[0]?.legs?.[0]?.start_location && (
          <Marker
            position={{
              lat: directionsResponse.routes[0].legs[0].start_location.lat,
              lng: directionsResponse.routes[0].legs[0].start_location.lng,
            }}
            label={{
              text: `Entrega: ${averageDeliveryTime.trim() === '' ? '15' : averageDeliveryTime} min`,
              color: 'black',
              fontSize: '12px',
            }}
            // Optionally offset the label by using anchor property if needed
          />
        )}
        {originMarker && <Marker position={originMarker} />}
        {destMarker && <Marker position={destMarker} />}
        {tollLocations.map((toll, idx) => (
          <Marker key={idx} position={{ lat: toll.lat, lng: toll.lng }} label={`Pedágio`} />
        ))}
        <Marker position={defaultCenter} />
        {comparisonResults.length > 0 && (
          <div style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '6px', padding: '10px', width: '260px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
            <h4 style={{ marginTop: 0 }}><strong>Comparação de Rotas</strong></h4>
            {/* UPDATED: Display selected vehicle icon, name and cargo weight in the panel if available */}
            {selectedVehicle && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '10px' }}>
                {React.createElement(vehicleIcons[selectedVehicle.type], { className: "text-xl" })}
                <span>{formatVehicleName(selectedVehicle.type)}</span>
                {cargoWeight && <span>({cargoWeight} kg)</span>}
              </div>
            )}
            {parseFloat(costKm) > 0 && (
              <p style={{ margin: '4px 0' }}>Preço/Km: R$ {parseFloat(costKm).toFixed(2)}</p>
            )}
            {parseFloat(dailyDriver) > 0 && (
              <p style={{ margin: '4px 0' }}>Diária Motorista: R$ {parseFloat(dailyDriver).toFixed(2)}</p>
            )}
            {parseFloat(dailyHelper) > 0 && (
              <p style={{ margin: '4px 0' }}>Diária Ajudante: R$ {parseFloat(dailyHelper).toFixed(2)}</p>
            )}
            {parseFloat(extraCosts) > 0 && (
              <p style={{ margin: '4px 0' }}>Custos Extras: R$ {parseFloat(extraCosts).toFixed(2)}</p>
            )}
            <div style={{ marginTop: '10px' }}>
              <strong>Kilometragem</strong>
              {comparisonResults.map((result, idx) => {
                const maxKm = Math.max(...comparisonResults.map(r => r.newKm));
                const kmPercent = maxKm ? (result.newKm / maxKm) * 100 : 0;
                const colors = ['#2196f3', '#ff9800', '#4caf50'];
                return (
                  <div key={idx} style={{ display: 'flex', margin: '5px 0', alignItems: 'center' }}>
                    <span style={{ width: '60px' }}>Rota {idx + 1}</span>
                    <div style={{ flexGrow: 1, marginRight: '5px', height: '8px', backgroundColor: '#eee', borderRadius: '4px' }}>
                      <div style={{ width: `${kmPercent}%`, backgroundColor: colors[idx], height: '100%', borderRadius: '4px' }}/>
                    </div>
                    <span>{result.newKm.toFixed(2)} km</span>
                  </div>
                );
              })}
            </div>
            {comparisonResults.some(r => r.newTotal > 0) && (
              <div style={{ marginTop: '10px' }}>
                <strong>Total (R$)</strong>
                {comparisonResults.map((result, idx) =>
                  result.newTotal > 0 ? (
                    <div key={idx} style={{ display: 'flex', margin: '5px 0', alignItems: 'center' }}>
                      <span style={{ width: '60px' }}>Rota {idx + 1}</span>
                      <div style={{ flexGrow: 1, marginRight: '5px', height: '8px', backgroundColor: '#eee', borderRadius: '4px' }}>
                        { /* Render bar if total is greater than 0 */ }
                        <div style={{ width: `${(result.newTotal / Math.max(...comparisonResults.map(r => r.newTotal))) * 100}%`, backgroundColor: ['#f44336', '#4caf50', '#2196f3'][idx], height: '100%', borderRadius: '4px' }}/>
                      </div>
                      <span>R$ {result.newTotal.toFixed(2)}</span>
                    </div>
                  ) : null
                )}
              </div>
            )}
            <div style={{ marginTop: '10px' }}>
              <strong>Tempo Total</strong>
              {comparisonResults[routeIndex] && (() => {
                const result = comparisonResults[routeIndex];
                const totalMinutes = result.totalTime;
                const totalMinutesRounded = Math.ceil(totalMinutes);
                const hours = Math.floor(totalMinutesRounded / 60);
                const minutes = totalMinutesRounded % 60;
                const totalTimeStr = `${hours}h ${minutes}min`;
                const [sHour, sMin] = startTime.split(':').map(Number);
                const availableTime = 17 * 60 - (sHour * 60 + sMin);
                const diff = availableTime - totalMinutes;
                let statusMsg = '';
                let statusColor = '';
                if (diff >= 30) {
                  statusMsg = "A rota pode ser feita no tempo";
                  statusColor = 'green';
                } else if (diff > 0) {
                  statusMsg = "Pode ser que não dê";
                  statusColor = 'orange';
                } else {
                  statusMsg = "Entrega impossível";
                  statusColor = 'red';
                }
                const progressPercent = Math.min(100, (totalMinutes / availableTime) * 100);
                const startTotalMinutes = sHour * 60 + sMin;
                const finishTotalMinutes = startTotalMinutes + totalMinutesRounded;
                const finishHour = Math.floor(finishTotalMinutes / 60);
                const finishMin = finishTotalMinutes % 60;
                const predictedFinish = `${finishHour < 10 ? "0" + finishHour : finishHour}:${finishMin < 10 ? "0" + finishMin : finishMin} h`;
                return (
                  <div style={{ margin: '5px 0' }}>
                    <span>Rota {routeIndex + 1}: {totalTimeStr}</span>
                    <br />
                    <div style={{ textAlign: 'center', color: statusColor, fontWeight: 'bold' }}>
                      {statusMsg}
                    </div>
                    {/* Progress bar with start & end times */}
                    <div style={{ marginTop: '5px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                        <span>{startTime}</span>
                        <span>17h</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', backgroundColor: '#eee', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${progressPercent}%`, backgroundColor: statusColor, height: '100%' }}></div>
                      </div>
                    </div>
                    {/* Display predicted finish time */}
                    <div style={{ marginTop: '4px', textAlign: 'center', fontSize: '12px', color: '#555' }}>
                      Hora prevista: {predictedFinish}
                    </div>
                    {/* New export section */}
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ marginRight: "10px" }}>
                        Formato:
                        <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} style={{ marginLeft: "5px" }}>
                          <option value="">Selecione</option>
                          <option value="json">JSON</option>
                          <option value="csv">CSV</option>
                          <option value="excell">Excell</option>
                        </select>
                      </label>
                      <button
                        onClick={handleExport}
                        disabled={!exportFormat}
                        style={{
                          marginTop: "10px",
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "4px",
                          backgroundColor: exportFormat ? "green" : "grey",
                          color: "#fff",
                          cursor: exportFormat ? "pointer" : "not-allowed"
                        }}
                      >
                        Exportar Rota
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </GoogleMap>
    </LoadScript>
  );
}
